import { inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

/**
 * Scrolls the window to the top when navigation lands on a different PATH.
 *
 * `scrollPositionRestoration` is deliberately off (`app.config.ts`): `RouterScroller` treats a
 * query-param-only navigation as a new page, and the campaign-detail and billing tab switches are
 * exactly that — enabling it would throw the operator to the top on every tab change. The cost was
 * the other half: a real page change kept the old scroll offset, so opening a campaign from a
 * scrolled ledger showed its detail page already scrolled down. This restores just that half.
 *
 * Left alone on purpose:
 * - same-path navigations (tab switches, filters) — the reason restoration is off at all;
 * - navigations with a fragment — anchor scrolling owns those (`/campaigns#autopilot`);
 * - back/forward (`popstate`) — the browser's own restoration gets its chance there.
 */
@Injectable({ providedIn: 'root' })
export class ScrollTopOnNavigationService {
  private readonly router = inject(Router);

  initialize(): void {
    let lastPath: string | null = null;
    let isPopstate = false;

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        isPopstate = event.navigationTrigger === 'popstate';
        return;
      }
      if (!(event instanceof NavigationEnd)) return;

      const url = new URL(event.urlAfterRedirects, window.location.origin);
      const pathChanged = lastPath !== null && url.pathname !== lastPath;
      lastPath = url.pathname;

      if (pathChanged && !url.hash && !isPopstate) {
        window.scrollTo(0, 0);
      }
    });
  }
}
 